import React, { useState } from 'react'
import EditOrdersModal from './orders-modal/EditOrdersModal'
import CreateOrderModal from './orders-modal/CreateOrderModal'
import {
  useGetOrdersQuery,
  useDeleteOrderMutation,
} from '../../../../store/api/ordersApi'
import { IOrder, IOrderResponse } from '../../../../types/orders'
import { IProduct } from '../../../../types/products'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faEdit, faTrashCan } from '@fortawesome/free-solid-svg-icons'
import { Store } from '../../../../utils/toast'
import { Button, Pagination } from '@mui/material'
import ConfirmDialog from '../../../../components/confirm-dialog/ConfirmDialog'
import Loading from '../../../../assets/icons/loading.svg?react'
import './OrdersPage.scss'

const c = 'orders-page'
const ORDERS_PER_PAGE = 10

const OrdersPage = () => {
  const [page, setPage] = useState(1)
  const { data, isLoading, isFetching, isError } = useGetOrdersQuery({
    page,
    limit: ORDERS_PER_PAGE,
  })
  const orders = data?.data ?? []
  const totalPages = data?.pagination?.totalPages ?? 1
  const [deleteOrder] = useDeleteOrderMutation()
  const [openEdit, setOpenEdit] = useState(false)
  const [openCreate, setOpenCreate] = useState(false)
  const [order, setOrder] = useState<IOrder>()
  const [openConfirm, setOpenConfirm] = useState(false)
  const [orderToDelete, setOrderToDelete] = useState<IOrder | undefined>()

  function handleDelete(orderId: string) {
    deleteOrder(orderId)
      .unwrap()
      .then(() => {
        Store.addNotification({
          title: 'SUCCESS',
          message: 'Success',
          container: 'top-center',
          type: 'success',
          insert: 'top',
          animationIn: ['animate__animated', 'animate__fadeIn'],
          animationOut: ['animate__animated', 'animate__fadeOut'],
          dismiss: {
            duration: 1000,
            onScreen: true,
          },
        })
      })
      .finally(() => {
        setOpenConfirm(false)
      })
  }

  function handleEdit(order: IOrder) {
    setOpenEdit(true)
    setOrder(order)
  }

  function openConfirmModal(order: IOrder) {
    setOpenConfirm(true)
    setOrderToDelete(order)
  }

  function handlePageChange(_: React.ChangeEvent<unknown>, value: number) {
    setPage(value)
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const showEmpty =
    !isLoading && !isFetching && (isError || orders.length === 0)

  return (
    <div className={c}>
      <div className={`${c}__header`}>
        <div className={`${c}__header-title`}>Comenzi</div>
        <Button
          variant='contained'
          onClick={() => setOpenCreate(true)}
          sx={{
            backgroundColor: 'var(--color-primary)',
            textTransform: 'none',
            fontWeight: 700,
            borderRadius: '8px',
            '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
          }}
        >
          + Adaugă
        </Button>
      </div>
      {(isLoading || isFetching) && (
        <div className={`${c}__loading`}>
          <Loading />
        </div>
      )}
      {showEmpty && (
        <div className={`${c}__empty`}>Nu au fost găsite comenzi</div>
      )}
      {!isLoading &&
        !isFetching &&
        orders?.map((item: IOrderResponse, index: number) => {
          return (
            <div key={index} className={`${c}__order`}>
              <div className={`${c}__order-title`}>
                {item.date == new Date().toLocaleDateString()
                  ? 'Astazi'
                  : item.date}
              </div>
              <div className={`${c}__order-items`}>
                {item.orders.map((order: IOrder, index: number) => (
                  <div key={index} className={`${c}__order-items-item`}>
                    <div className={`${c}__order-items-item-actions`}>
                      <div className={`${c}__order-items-item-actions-action`}>
                        <FontAwesomeIcon
                          icon={faEdit}
                          onClick={() => handleEdit(order)}
                        />
                      </div>
                      <div className={`${c}__order-items-item-actions-action`}>
                        <FontAwesomeIcon
                          icon={faTrashCan}
                          onClick={() => openConfirmModal(order)}
                        />
                      </div>
                    </div>
                    <div className={`${c}__order-items-item-details`}>
                      <div className={`${c}__order-items-item-details-row`}>
                        <span
                          className={`${c}__order-items-item-details-row-bold`}
                        >
                          {order.delivery_method}
                        </span>{' '}
                        - <span>{order.location}</span>
                      </div>
                      <div className={`${c}__order-items-item-details-row`}>
                        Ora: {order.time}
                      </div>
                      <div className={`${c}__order-items-item-details-row`}>
                        Nume: {order.name}
                      </div>
                      <div className={`${c}__order-items-item-details-row`}>
                        Telefon: {order.phone}
                      </div>
                      <div className={`${c}__order-items-item-details-row`}>
                        Metoda de plata: {order.payment}
                      </div>
                      <div className={`${c}__order-items-item-details-row`}>
                        Pretul Livrarii: {order.delivery_cost}
                      </div>
                      <div className={`${c}__order-items-item-details-row`}>
                        Pretul Comenzii: {order.order_price}
                      </div>
                      <div className={`${c}__order-items-item-details-row`}>
                        Detalii: {order.details}
                      </div>
                      <div className={`${c}__order-items-item-details-row`}>
                        <span
                          className={`${c}__order-items-item-details-row-bold`}
                        >
                          TOTAL: {order.total_price}
                        </span>
                      </div>
                    </div>
                    <div className={`${c}__order-items-item-cart`}>
                      <div className={`${c}__order-items-item-cart-title`}>
                        Comanda:
                      </div>
                      {order.cart.map((product: IProduct, index: number) => (
                        <div
                          key={index}
                          className={`${c}__order-items-item-cart-item`}
                        >
                          <div
                            className={`${c}__order-items-item-cart-item-name`}
                          >
                            {product.name} x {product.quantity}
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )
        })}
      <EditOrdersModal isOpen={openEdit} setIsOpen={setOpenEdit} data={order} />
      <CreateOrderModal isOpen={openCreate} setIsOpen={setOpenCreate} />
      <ConfirmDialog
        open={openConfirm}
        title='Șterge comandă'
        message={`Ești sigur că vrei să ștergi comanda ${orderToDelete?.name ?? ''}?`}
        onConfirm={() => {
          if (orderToDelete?._id) handleDelete(orderToDelete._id)
          setOpenConfirm(false)
        }}
        onCancel={() => setOpenConfirm(false)}
      />
      {totalPages > 1 && (
        <div
          className={`${c}__pagination`}
          style={{ display: isFetching ? 'none' : undefined }}
        >
          <Pagination
            count={totalPages}
            page={page}
            onChange={handlePageChange}
            shape='rounded'
            sx={{
              '& .MuiPaginationItem-root': {
                color: '#111',
              },
              '& .MuiPaginationItem-root.Mui-selected': {
                backgroundColor: 'var(--color-primary)',
                color: '#fff',
                '&:hover': { backgroundColor: 'var(--color-primary-hover)' },
              },
            }}
          />
        </div>
      )}
    </div>
  )
}

export default OrdersPage
